import { useEffect, useState } from "react";
import useSWR, { mutate } from "swr";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import client, { fetcher } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  FileText,
  Loader2,
  Download,
  Eye,
  RotateCcw,
  Save,
  Receipt,
  ShieldCheck,
  Info,
  Copy,
  BarChart3,
} from "lucide-react";

/**
 * Pestaña "Textos institucionales" (Fase 4).
 * - Editor bilingüe del financing_statement (ES + GL)
 * - Card de descarga de Memoria PDF (4 páginas · ES/GL · Vista previa · HMAC)
 * - Card de descarga de la mención factura (1 página · ES/GL)
 * Sólo ADMIN — la protección la aplica el wrapper de Admin.jsx.
 */
export default function TextosInstitucionales() {
  const { i18n } = useTranslation();
  const es = i18n.language?.startsWith("es");
  const { data: settings } = useSWR("/admin/settings", fetcher);

  const [formEs, setFormEs] = useState("");
  const [formGl, setFormGl] = useState("");
  const [saving, setSaving] = useState(false);
  const [busyDoc, setBusyDoc] = useState(null); // "mem-es" | "mem-gl" | "fac-es" | "fac-gl" | "preview-es" | "preview-gl"

  useEffect(() => {
    if (settings) {
      setFormEs(settings.financing_statement_es || "");
      setFormGl(settings.financing_statement_gl || "");
    }
  }, [settings]);

  const isDefaultEs = !!settings?.financing_statement_es_is_default;
  const isDefaultGl = !!settings?.financing_statement_gl_is_default;
  const dirty =
    settings &&
    (formEs !== (settings.financing_statement_es || "") ||
      formGl !== (settings.financing_statement_gl || ""));

  const save = async () => {
    setSaving(true);
    try {
      const body = {};
      if (formEs !== settings.financing_statement_es) body.financing_statement_es = formEs;
      if (formGl !== settings.financing_statement_gl) body.financing_statement_gl = formGl;
      await client.patch("/admin/settings", body);
      toast.success(es ? "Textos guardados" : "Textos gardados");
      mutate("/admin/settings");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally {
      setSaving(false);
    }
  };

  const restoreDefault = async (which) => {
    // Sending empty string = $unset in Mongo → falls back to memoria_i18n default.
    setSaving(true);
    try {
      const body =
        which === "es"
          ? { financing_statement_es: "" }
          : { financing_statement_gl: "" };
      await client.patch("/admin/settings", body);
      toast.success(es ? "Restaurado al valor por defecto" : "Restaurado ao valor por defecto");
      mutate("/admin/settings");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally {
      setSaving(false);
    }
  };

  const downloadPdf = async (path, filename, key) => {
    setBusyDoc(key);
    try {
      const token = sessionStorage.getItem("salnes_token");
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}${path}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const hmac = res.headers.get("X-Salnes-Memoria-HMAC");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(
        hmac
          ? `${es ? "Descargado" : "Descargado"} · HMAC ${hmac.slice(0, 16)}…`
          : es ? "Descargado" : "Descargado",
      );
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusyDoc(null);
    }
  };

  const previewPdf = async (lang) => {
    setBusyDoc(`preview-${lang}`);
    try {
      const token = sessionStorage.getItem("salnes_token");
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/memoria/pdf?lang=${lang}&inline=true`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener,noreferrer");
      // Delay revoke so the browser has time to open.
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusyDoc(null);
    }
  };

  if (!settings) {
    return (
      <div className="text-sm text-salnes-stone italic" data-testid="textos-loading">
        {es ? "Cargando…" : "Cargando…"}
      </div>
    );
  }

  return (
    <div data-testid="textos-institucionales-root" className="space-y-8">
      {/* Aviso */}
      <div
        className="rounded-2xl border border-amber-200 bg-amber-50 p-4 flex items-start gap-3"
        data-testid="textos-warning"
      >
        <Info size={18} className="text-amber-700 mt-0.5 shrink-0" />
        <div className="text-sm text-amber-900 leading-snug">
          {es
            ? "Este texto aparece en la memoria PDF, en el pie de página pública y en la plantilla de factura acompañante. Sobrescríbelo con el literal exacto de la resolución de adjudicación cuando la tengas."
            : "Este texto aparece na memoria PDF, no pé de páxina público e na plantilla de factura acompañante. Sobreescríbeo co literal exacto da resolución de adxudicación cando a teñas."}
        </div>
      </div>

      {/* Editor bilingüe */}
      <section
        className="rounded-3xl border hairline bg-white p-6"
        data-testid="textos-editor"
      >
        <div className="flex items-center gap-2 mb-1">
          <ShieldCheck size={18} className="text-salnes-atlantic" />
          <h2 className="font-serif text-2xl text-salnes-ink">
            {es ? "Mención al financiador" : "Mención ao financiador"}
          </h2>
        </div>
        <p className="text-sm text-salnes-stone mb-5">
          {es
            ? "Texto oficial que se muestra en memoria, factura y footer público. Se aplica al instante."
            : "Texto oficial que se amosa en memoria, factura e footer público. Aplícase ao instante."}
        </p>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <FieldBox
            label={es ? "Texto en castellano (ES)" : "Texto en castelán (ES)"}
            value={formEs}
            onChange={setFormEs}
            isDefault={isDefaultEs}
            onRestore={() => restoreDefault("es")}
            testid="textos-editor-es"
            langLabelDefault={es ? "por defecto" : "por defecto"}
            restoreLabel={es ? "Restaurar por defecto" : "Restaurar por defecto"}
          />
          <FieldBox
            label={es ? "Texto en gallego (GL)" : "Texto en galego (GL)"}
            value={formGl}
            onChange={setFormGl}
            isDefault={isDefaultGl}
            onRestore={() => restoreDefault("gl")}
            testid="textos-editor-gl"
            langLabelDefault={es ? "por defecto" : "por defecto"}
            restoreLabel={es ? "Restaurar por defecto" : "Restaurar por defecto"}
          />
        </div>
        <div className="flex justify-end mt-5">
          <Button
            onClick={save}
            disabled={saving || !dirty}
            className="rounded-full bg-salnes-atlantic hover:bg-salnes-atlantic/90"
            data-testid="textos-save-btn"
          >
            {saving ? (
              <Loader2 size={13} className="mr-2 animate-spin" />
            ) : (
              <Save size={13} className="mr-2" />
            )}
            {es ? "Guardar textos" : "Gardar textos"}
          </Button>
        </div>
      </section>

      {/* Memoria PDF card */}
      <section
        className="rounded-3xl border hairline bg-white p-6"
        data-testid="textos-memoria-card"
      >
        <div className="flex items-center gap-2 mb-1">
          <FileText size={18} className="text-salnes-atlantic" />
          <h2 className="font-serif text-2xl text-salnes-ink">
            {es ? "Memoria institucional (PDF · 4 páginas)" : "Memoria institucional (PDF · 4 páxinas)"}
          </h2>
        </div>
        <p className="text-sm text-salnes-stone mb-5 max-w-3xl">
          {es
            ? "Documento oficial que acompaña a la factura del proyecto. KPIs actualizados al momento de la descarga. Incluye franja de logos institucionales, texto de financiación y sello criptográfico HMAC-SHA256 verificable."
            : "Documento oficial que acompaña á factura do proxecto. KPIs actualizados no momento da descarga. Inclúe franxa de logos institucionais, texto de financiamento e selo criptográfico HMAC-SHA256 verificable."}
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <DocButton
            title={es ? "Descargar ES" : "Descargar ES"}
            icon={<Download size={16} />}
            busy={busyDoc === "mem-es"}
            onClick={() =>
              downloadPdf(
                "/api/admin/memoria/pdf?lang=es",
                `memoria-salnes-sostenible-es-${new Date().toISOString().slice(0, 10)}.pdf`,
                "mem-es",
              )
            }
            testid="mem-download-es"
          />
          <DocButton
            title={es ? "Descargar GL" : "Descargar GL"}
            icon={<Download size={16} />}
            busy={busyDoc === "mem-gl"}
            onClick={() =>
              downloadPdf(
                "/api/admin/memoria/pdf?lang=gl",
                `memoria-salnes-sostible-gl-${new Date().toISOString().slice(0, 10)}.pdf`,
                "mem-gl",
              )
            }
            testid="mem-download-gl"
          />
        </div>
        <div className="flex flex-wrap gap-2 mt-3">
          <Button
            variant="outline"
            size="sm"
            onClick={() => previewPdf("es")}
            disabled={busyDoc === "preview-es"}
            className="rounded-full"
            data-testid="mem-preview-es"
          >
            {busyDoc === "preview-es" ? (
              <Loader2 size={13} className="mr-2 animate-spin" />
            ) : (
              <Eye size={13} className="mr-2" />
            )}
            {es ? "Vista previa (ES)" : "Vista previa (ES)"}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => previewPdf("gl")}
            disabled={busyDoc === "preview-gl"}
            className="rounded-full"
            data-testid="mem-preview-gl"
          >
            {busyDoc === "preview-gl" ? (
              <Loader2 size={13} className="mr-2 animate-spin" />
            ) : (
              <Eye size={13} className="mr-2" />
            )}
            {es ? "Vista previa (GL)" : "Vista previa (GL)"}
          </Button>
        </div>
      </section>

      {/* Informe ejecutivo ESG PDF card (iter47) */}
      <section
        className="rounded-3xl border hairline bg-white p-6"
        data-testid="textos-informe-esg-card"
      >
        <div className="flex items-center gap-2 mb-1">
          <BarChart3 size={18} className="text-salnes-emerald" />
          <h2 className="font-serif text-2xl text-salnes-ink">
            {es
              ? "Informe ejecutivo ESG (PDF · 8 páginas)"
              : "Informe executivo ESG (PDF · 8 páxinas)"}
          </h2>
        </div>
        <p className="text-sm text-salnes-stone mb-5 max-w-3xl">
          {es
            ? "Anexo técnico con los 8 indicadores ESG que hoy publica la plataforma: fórmula, unidad, fuente, referencia normativa (GRI · ISO 14064 · MITECO), valor actual en vivo y método de cálculo (real vs estimación mínima plausible). Uso interno · dirección financiera."
            : "Anexo técnico cos 8 indicadores ESG que hoxe publica a plataforma: fórmula, unidade, fonte, referencia normativa (GRI · ISO 14064 · MITECO), valor actual en vivo e método de cálculo (real vs estimación mínima plausible). Uso interno · dirección financeira."}
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <DocButton
            title={es ? "Descargar ES" : "Descargar ES"}
            icon={<Download size={16} />}
            busy={busyDoc === "esg-es"}
            onClick={() =>
              downloadPdf(
                "/api/admin/informe-esg/pdf?lang=es",
                `informe-esg-salnes-es-${new Date().toISOString().slice(0, 10)}.pdf`,
                "esg-es",
              )
            }
            testid="esg-download-es"
          />
          <DocButton
            title={es ? "Descargar GL" : "Descargar GL"}
            icon={<Download size={16} />}
            busy={busyDoc === "esg-gl"}
            onClick={() =>
              downloadPdf(
                "/api/admin/informe-esg/pdf?lang=gl",
                `informe-esg-salnes-gl-${new Date().toISOString().slice(0, 10)}.pdf`,
                "esg-gl",
              )
            }
            testid="esg-download-gl"
          />
        </div>
        <div className="flex flex-wrap gap-2 mt-3">
          {[
            { lang: "es", label: es ? "Copiar enlace público (ES)" : "Copiar ligazón pública (ES)", testid: "esg-copy-public-es" },
            { lang: "gl", label: es ? "Copiar enlace público (GL)" : "Copiar ligazón pública (GL)", testid: "esg-copy-public-gl" },
          ].map((btn) => (
            <Button
              key={btn.testid}
              variant="outline"
              size="sm"
              onClick={() => {
                const url = `${window.location.origin}/api/public/informe-esg/pdf?lang=${btn.lang}`;
                if (navigator.clipboard?.writeText) {
                  navigator.clipboard.writeText(url).then(
                    () => toast.success(es ? "Enlace copiado al portapapeles" : "Ligazón copiada ao portapapeis"),
                    () => toast.error(es ? "No se pudo copiar" : "Non se puido copiar"),
                  );
                } else {
                  toast.error(url);
                }
              }}
              className="rounded-full"
              data-testid={btn.testid}
            >
              <Copy size={13} className="mr-2" />
              {btn.label}
            </Button>
          ))}
        </div>
      </section>

      {/* Mención factura card */}
      <section
        className="rounded-3xl border hairline bg-white p-6"
        data-testid="textos-factura-card"
      >
        <div className="flex items-center gap-2 mb-1">
          <Receipt size={18} className="text-salnes-atlantic" />
          <h2 className="font-serif text-2xl text-salnes-ink">
            {es ? "Mención en factura (PDF · 1 página)" : "Mención en factura (PDF · 1 páxina)"}
          </h2>
        </div>
        <p className="text-sm text-salnes-stone mb-5 max-w-3xl">
          {es
            ? "Documento de una sola página para adjuntar cuando la plataforma de facturación electrónica no permita incluir el bloque de texto completo del financiador."
            : "Documento dunha soa páxina para adxuntar cando a plataforma de facturación electrónica non permita incluír o bloque de texto completo do financiador."}
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <DocButton
            title={es ? "Descargar mención (ES)" : "Descargar mención (ES)"}
            icon={<Download size={16} />}
            busy={busyDoc === "fac-es"}
            onClick={() =>
              downloadPdf(
                "/api/admin/factura/plantilla-mencion.pdf?lang=es",
                `mencion-factura-salnes-es-${new Date().toISOString().slice(0, 10)}.pdf`,
                "fac-es",
              )
            }
            testid="fac-download-es"
          />
          <DocButton
            title={es ? "Descargar mención (GL)" : "Descargar mención (GL)"}
            icon={<Download size={16} />}
            busy={busyDoc === "fac-gl"}
            onClick={() =>
              downloadPdf(
                "/api/admin/factura/plantilla-mencion.pdf?lang=gl",
                `mencion-factura-salnes-gl-${new Date().toISOString().slice(0, 10)}.pdf`,
                "fac-gl",
              )
            }
            testid="fac-download-gl"
          />
        </div>
      </section>
    </div>
  );
}

function FieldBox({ label, value, onChange, isDefault, onRestore, testid, langLabelDefault, restoreLabel }) {
  return (
    <div className="flex flex-col">
      <div className="flex items-center justify-between mb-2">
        <Label className="text-[10px] uppercase tracking-[0.2em] text-salnes-stone">{label}</Label>
        {isDefault && (
          <span className="text-[10px] text-salnes-atlantic italic" data-testid={`${testid}-is-default`}>
            {langLabelDefault}
          </span>
        )}
      </div>
      <textarea
        className="w-full min-h-[220px] rounded-xl border border-salnes-stone/20 bg-white p-3 text-sm leading-relaxed text-salnes-ink focus:border-salnes-atlantic outline-none"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
      />
      <button
        type="button"
        onClick={onRestore}
        className="self-start mt-2 text-[11px] text-salnes-stone hover:text-salnes-atlantic inline-flex items-center gap-1"
        data-testid={`${testid}-restore`}
      >
        <RotateCcw size={11} /> {restoreLabel}
      </button>
    </div>
  );
}

function DocButton({ title, icon, busy, onClick, testid }) {
  return (
    <button
      onClick={onClick}
      disabled={busy}
      className="rounded-2xl border hairline bg-white hover:bg-salnes-ivory transition-colors p-5 text-left flex items-center gap-4 disabled:opacity-60 disabled:cursor-wait"
      data-testid={testid}
    >
      <div className="h-11 w-11 rounded-full bg-salnes-atlantic/10 text-salnes-atlantic flex items-center justify-center">
        {busy ? <Loader2 size={18} className="animate-spin" /> : icon}
      </div>
      <div className="flex-1">
        <div className="font-medium text-salnes-ink">{title}</div>
        <div className="text-[11px] text-salnes-stone mt-0.5">PDF · A4</div>
      </div>
    </button>
  );
}
